import { Component, Input, inject } from '@angular/core';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { BaseWidgetComponent } from '../../../../../home/home-widgets/widgets/base-widget.component';
import { AuthService, FilterService } from '../../../../../_shared/services';
import { FuelCostService } from '../../../services/fuel-cost.service';
import { FuelCostKpiRequest } from '../../../models/fuel-cost-kpi-request.model';
import { FuelType } from '../../../../_shared/enums/fuel-type.enum';
import { formatCostDifference, getEnumFromString } from '../../../../../_shared/helpers/kpiWidget/kpi-formatters.helper';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { isUptrendPositive } from '../../../../../_shared/helpers/kpiWidget/dropdown-measurement.helper';
import { CurrencyHelper } from '../../../../../_shared/helpers/currency/currency-map.helper';
import { DropdownOption, KpiWidgetComponent } from '../../../../../_shared/components/kpi-widget/kpi-widget.component';
import { MeasurementTypes } from '../../../../../_shared/enums/kpiStats/measurement-types.enum';

@Component({
  selector: 'app-fuel-cost-widget',
  standalone: true,
  imports: [KpiWidgetComponent, TranslocoPipe],
  templateUrl: './fuel-cost-widget.component.html',
  styleUrl: './fuel-cost-widget.component.scss'
})
export class FuelCostWidgetComponent extends BaseWidgetComponent {

  @Input() selectedFilter: string;

  private transloco = inject(TranslocoService);

  cost: number = 10;
  icon = 'assets/images-fuel/costIcon.svg';
  hasError: boolean = false;
  selectedUnit = FuelType.Diesel.toString();
  errorMessage: string = '';
  currencyCode: string = '';
  currencySymbol: string = '';

  dropdownOptions: DropdownOption[] = [
    { label: this.transloco.translate('fuelDashboard.units.diesel'), value: FuelType.Diesel.toString(), unit: '' },
    { label: this.transloco.translate('fuelDashboard.units.petrol'), value: FuelType.Petrol.toString(), unit: '' }
  ];

  constructor(
    filterService: FilterService,
    private fuelCostService: FuelCostService,
    private errorHandlerService: ErrorHandlerService,
    private authService: AuthService
  ) {
    super(filterService);
    this.currencyCode = this.authService.getUserSettings()?.currencyCode ?? 'ZAR'
    this.currencySymbol = CurrencyHelper.getCurrencySymbol(this.currencyCode);
  }

  protected override async onFilterChangeAsync(): Promise<void> {
    await this.getFuelCostSummaryData();
  }

  public async onDropdownChanged(selectedOption: DropdownOption) {
    this.selectedUnit = selectedOption.value;
    this.isLoading = true;
    await this.onFilterChangeAsync();
  }

  get isUptrendPositive(): boolean {
    return isUptrendPositive(getEnumFromString(this.selectedUnit));
  }

  private async getFuelCostSummaryData(): Promise<void> {
    this.isLoading = true;
    this.hasError = false;

    try {
      const request: FuelCostKpiRequest = {
        fuelType: this.getEnumFromString(this.selectedUnit),
        accountIds: this.widgetFilter.accountIds,
        timeFrom: this.widgetFilter.startDate!,
        timeTo: this.widgetFilter.endDate!,
        previousTimeFrom: this.widgetFilter.previousCycleStartDate!,
        previousTimeTo: this.widgetFilter.previousCycleEndDate!
      };

      this.cancelSubject.next();
      const response = await this.fuelCostService.getFuelCostSummary(request, this.cancelSubject);
      if (response && response.data) {
        this.cost = response.data.totalCost;
        this.value = response.data.totalValue;
        this.percentageDifference = response.data.percentageChange;
        this.difference = response.data.difference;
      }

      this.isLoading = false;
    } catch (error) {
      this.errorHandlerService.handleError(error, {
        callback: (message) => {
          this.errorMessage = message;
          this.hasError = true;
          this.isLoading = false;
        }
      });
    }
  }

  public override formatValue(value: number | null | undefined): string {
    if (value == null) {
      return this.transloco.translate('fuelShared.charts.notAvailable');
    }

    return `${this.currencySymbol}${value.toFixed(2)}/ℓ`;
  }

  public formatCost(): string {
    return this.currencyCode;
  }

  formatCostDifference(value: number): string {
    return formatCostDifference(value, this.selectedFilter, this.currencySymbol, (key, params) => this.transloco.translate(key, params));
  }

  public onWidgetClick(): void {
  }

  private getEnumFromString(value: string): FuelType {
    const enumValue = parseInt(value) as FuelType;
    const isValid = Object.values(FuelType).includes(enumValue);
    return isValid ? enumValue : FuelType.Diesel;
  }
}
